import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Navbar from "../navbar/navbar";
import Swal from "sweetalert2";
import { changePassword } from "../../services/admin.service";
import { FaEye, FaEyeSlash, FaLock } from "react-icons/fa";
import "../admin/adminModule.css";
import { logout, logoutUser } from "../../services/auth.service";
import { useNavigate } from "react-router-dom";

const ChangePasswordComponent = () => {

    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState({
        current: false,
        new: false,
        confirm: false,
    });

    const toggleVisibility = (field) => {
        setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
    };


    const initialValues = {
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
    };

    const validationSchema = Yup.object({
        currentPassword: Yup.string().required("Current password is required"),
        newPassword: Yup.string()
            .required("New password is required")
            .min(8, "Password must be at least 8 characters")
            .matches(/[A-Z]/, "Must include at least one uppercase letter")
            .matches(/[a-z]/, "Must include at least one lowercase letter")
            .matches(/[0-9]/, "Must include at least one number")
            .matches(/[@$!%*?&]/, "Must include at least one special character")
            .notOneOf([Yup.ref("currentPassword")], "New password must be different from current password"),
        confirmPassword: Yup.string()
            .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
            .required("Confirm password is required"),
    });

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        try {
            const response = await changePassword(values);

            if (response?.data?.message) {
                Swal.fire({
                    icon: "success",
                    title: "Success",
                    text: response.data.message,
                });
                resetForm();
                await logoutUser();
                logout("L");
                navigate("/login", { replace: true });
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: "Password change was unsuccessful.",
                });
            }
        } catch (error) {
            Swal.fire({
                icon: "error",
                title: "Error",
                text: error?.response?.data?.message || "Something went wrong. Please try again.",
            });
        } finally {
            setSubmitting(false);
        }
    };


    return (
        <div>
            <Navbar />
            <div className="container d-flex justify-content-center align-items-center p-4 mt-4 cs-container-width">
                <div className="col-md-6 col-lg-5 col-xl-4 w-100 text-start">
                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                    >
                        {({ isSubmitting }) => (
                            <Form className="card p-4 shadow-lg border-0 rounded-4">
                                <div className="text-center mb-4">
                                    <FaLock size={40} className="text-primary mb-2" />
                                    <h4 className="fw-bold">Change Password</h4>
                                    <p className="text-muted small mb-0">
                                        Keep your account secure by using a strong password.
                                    </p>
                                </div>

                                {/* Current Password */}
                                <div className="mb-3 position-relative">
                                    <label className="form-label fw-semibold">
                                        Current Password
                                    </label>
                                    <div className="input-group">
                                        <Field
                                            type={showPassword.current ? "text" : "password"}
                                            name="currentPassword"
                                            className="form-control"
                                            placeholder="Enter current password"
                                        />
                                        <span
                                            className="input-group-text cs-pointer"
                                            onClick={() => toggleVisibility("current")}
                                        >
                                            {showPassword.current ? <FaEyeSlash /> : <FaEye />}
                                        </span>
                                    </div>
                                    <ErrorMessage
                                        name="currentPassword"
                                        component="div"
                                        className="text-danger small mt-1"
                                    />
                                </div>

                                {/* New Password */}
                                <div className="mb-3 position-relative">
                                    <label className="form-label fw-semibold">New Password</label>
                                    <div className="input-group">
                                        <Field
                                            type={showPassword.new ? "text" : "password"}
                                            name="newPassword"
                                            className="form-control"
                                            placeholder="Enter new password"
                                        />
                                        <span
                                            className="input-group-text cs-pointer"
                                            onClick={() => toggleVisibility("new")}
                                        >
                                            {showPassword.new ? <FaEyeSlash /> : <FaEye />}
                                        </span>
                                    </div>
                                    <ErrorMessage
                                        name="newPassword"
                                        component="div"
                                        className="text-danger small mt-1"
                                    />

                                    {/* Password Requirements */}
                                    <ul className="text-muted small mt-2 mb-0 ps-3">
                                        <li>At least 8 characters</li>
                                        <li>One uppercase and one lowercase letter</li>
                                        <li>One number and one special character</li>
                                    </ul>
                                </div>

                                {/* Confirm Password */}
                                <div className="mb-4 position-relative">
                                    <label className="form-label fw-semibold">
                                        Confirm Password
                                    </label>
                                    <div className="input-group">
                                        <Field
                                            type={showPassword.confirm ? "text" : "password"}
                                            name="confirmPassword"
                                            className="form-control"
                                            placeholder="Re-enter new password"
                                        />
                                        <span
                                            className="input-group-text cs-pointer"
                                            onClick={() => toggleVisibility("confirm")}
                                        >
                                            {showPassword.confirm ? <FaEyeSlash /> : <FaEye />}
                                        </span>
                                    </div>
                                    <ErrorMessage
                                        name="confirmPassword"
                                        component="div"
                                        className="text-danger small mt-1"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    className="btn btn-primary w-100 fw-semibold py-2"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? "Changing..." : "Change Password"}
                                </button>
                            </Form>
                        )}
                    </Formik>
                </div>
            </div>
        </div>

    )
};

export default ChangePasswordComponent;